import React, { useState, useMemo } from 'react';
import CollegeCard from './CollegeCard';
import styles from './CollegeExplore.module.css';
import { collegeData } from '../utils/collegeData'; // Import the new manual data
import SearchBar from './SearchBar'; // Assuming you have a SearchBar component
import { searchColleges, getSearchSuggestions } from '../utils/searchUtils'; // Import the new search utilities

const CollegeExplore = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [acceptanceRateFilter, setAcceptanceRateFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Define acceptance rate ranges
  const acceptanceRateRanges = [
    { value: '1to25', label: '1-25%' },
    { value: '26to50', label: '26-50%' },
    { value: '51to75', label: '51-75%' },
    { value: '76to100', label: '76-100%' }
  ];

  // Define regions based on the colleges in the data
  const regions = useMemo(() => [
    { value: 'northeast', label: 'Northeast', states: ['MA', 'NY', 'CT', 'NJ', 'PA', 'RI', 'NH', 'MD', 'DE', 'VT', 'DC'] },
    { value: 'west', label: 'West', states: ['CA', 'WA', 'OR', 'AZ', 'CO', 'UT'] },
    { value: 'midwest', label: 'Midwest', states: ['IL', 'IN', 'WI', 'OH', 'MO', 'MI', 'MN', 'IA', 'NE'] },
    { value: 'south', label: 'South', states: ['TX', 'GA', 'NC', 'VA', 'TN', 'FL', 'SC', 'AL', 'LA', 'OK'] }
  ], []);

  const filteredColleges = useMemo(() => {
    let filtered = collegeData;

    // Apply robust search filter
    if (searchQuery) {
      filtered = searchColleges(collegeData, searchQuery);
    }

    // Apply acceptance rate filter
    if (acceptanceRateFilter) {
      filtered = filtered.filter((college) => {
        const rate = parseFloat(college.stats?.acceptanceRate || '0');
        // Only filter if we have valid acceptance rate data
        if (!college.stats?.acceptanceRate || isNaN(rate)) {
          return false; // Exclude colleges without valid acceptance rate data from filtered results
        }
        switch (acceptanceRateFilter) {
          case '1to25':
            return rate >= 1 && rate <= 25;
          case '26to50':
            return rate >= 26 && rate <= 50;
          case '51to75':
            return rate >= 51 && rate <= 75;
          case '76to100':
            return rate >= 76 && rate <= 100;
          default:
            return true;
        }
      });
    }

    // Apply region filter
    if (regionFilter) {
      const selectedRegion = regions.find(r => r.value === regionFilter);
      if (selectedRegion && selectedRegion.states) {
        filtered = filtered.filter((college) => {
          const locationParts = college.location.split(', ');
          const state = locationParts[locationParts.length - 1]; // Get the last part as state
          return selectedRegion.states.includes(state);
        });
      }
    }

    return filtered;
  }, [searchQuery, acceptanceRateFilter, regionFilter, regions]);

  // Handle search input changes
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    
    // Generate search suggestions
    if (query.trim()) {
      const suggestions = getSearchSuggestions(collegeData, query, 5);
      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle search input focus/blur
  const handleSearchFocus = () => {
    if (searchQuery.trim() && searchSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Explore Top Colleges.</h1>
        <p className={styles.subtitle}>
          Discover detailed profiles and statistics for leading universities.
        </p>
        <div className={styles.searchContainer}>
          <div className={styles.searchWrapper}>
            <SearchBar
              placeholder="Search for a college, location, or program..."
              onSearchChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              value={searchQuery}
            />
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className={styles.suggestionsContainer}>
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={styles.suggestionItem}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <h3>Filter Colleges</h3>
          <button 
            className={styles.clearButton}
            onClick={() => {
              setAcceptanceRateFilter('');
              setRegionFilter('');
            }}
          >
            Clear All Filters
          </button>
        </div>
        
        <div className={styles.filterGroups}>
          {/* Acceptance Rate Filter */}
          <div className={styles.filterGroup}>
            <h4>Acceptance Rate</h4>
            <div className={styles.filterOptions}>
              {acceptanceRateRanges.map((range) => (
                <button
                  key={range.value}
                  className={`${styles.filterButton} ${
                    acceptanceRateFilter === range.value ? styles.filterButtonActive : ''
                  }`}
                  onClick={() => setAcceptanceRateFilter(range.value)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Region Filter */}
          <div className={styles.filterGroup}>
            <h4>Region</h4>
            <div className={styles.filterOptions}>
              {regions.map((region) => (
                <button
                  key={region.value}
                  className={`${styles.filterButton} ${
                    regionFilter === region.value ? styles.filterButtonActive : ''
                  }`}
                  onClick={() => setRegionFilter(region.value)}
                >
                  {region.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className={styles.resultsSection}>
        <h3>Results ({filteredColleges.length} colleges)</h3>
        {searchQuery && (
          <p className={styles.searchInfo}>
            Showing results for "{searchQuery}" - including similar matches, nicknames, and locations
          </p>
        )}
        <div className={styles.collegeGrid}>
          {filteredColleges.length > 0 ? (
            filteredColleges.map((college) => (
              <CollegeCard key={college.name} college={college} />
            ))
          ) : (
            <p className={styles.noResults}>
              No colleges found matching your criteria. Try adjusting your search or filters.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollegeExplore;